import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getRandomKey } from "../hooks/useReload";
import {
    PlannedWorkShiftDto,
    PlannedWorkShiftQueryParameters,
} from "../models/plannedWorkShift/plannedWorkShift";
import { getPlannedWorkShifts } from "../services/plannedWorkShiftService";

interface WorkShiftPlanningFilters {
    selectedEmployeeIds: string[];
    selectedEmployeeGroupId: string;
    selectedSalaryPeriodTypeId: string;
    selectedSalaryPeriodId: string;
    startDate: string;
    endDate: string;
    showUnemployable: boolean;
}

interface WorkShiftPlanningState {
    reloadKey: number;
    routesReloadKey: number;
    filters: WorkShiftPlanningFilters;
    plannedWorkShifts: PlannedWorkShiftDto[];
    shiftsLoading: boolean;
    cardViewMode: "compact" | "expanded";
    rowExpanded: Record<string, boolean>;
}

export const initialState: WorkShiftPlanningState = {
    reloadKey: getRandomKey(),
    routesReloadKey: getRandomKey(),
    filters: {
        selectedEmployeeIds: [],
        selectedEmployeeGroupId: null,
        selectedSalaryPeriodTypeId: null,
        selectedSalaryPeriodId: null,
        startDate: null,
        endDate: null,
        showUnemployable: true
    },
    plannedWorkShifts: [],
    shiftsLoading: false,
    cardViewMode: "compact",
    rowExpanded: {},
};

export const workShiftPlanningSlice = createSlice({
    name: "workShiftPlanning",
    initialState: initialState,
    reducers: {
        setPlannedWorkShifts: (
            state,
            action: PayloadAction<PlannedWorkShiftDto[]>
        ) => {
            state.plannedWorkShifts = action.payload;
        },
        setPlannedWorkShiftsLoading: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.shiftsLoading = action.payload;
        },
        reloadPlannedWorkShifts: (state) => {
            state.reloadKey = getRandomKey();
        },
        reloadRoutes: (state) => {
            state.routesReloadKey = getRandomKey();
        },
        updateWorkShiftPlanningFilters: (
            state,
            action: PayloadAction<Partial<WorkShiftPlanningFilters>>
        ) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        setCardViewMode: (state, action: PayloadAction<"compact" | "expanded">) => {
            state.cardViewMode = action.payload;
        },
        toggleRowExpanded: (state, action: PayloadAction<string>) => {
            if (!state.rowExpanded) state.rowExpanded = {};
            const id = action.payload ?? "null";
            state.rowExpanded[id] = !state.rowExpanded[id];
        },
        setAllRowsExpanded: (state, action: PayloadAction<boolean>) => {
            state.cardViewMode = action.payload ? "expanded" : "compact";
            state.rowExpanded = {};
        },
    },
});

export const fetchPlannedWorkShifts = createAsyncThunk(
    "workShiftPlanning/list",
    async(params: PlannedWorkShiftQueryParameters, thunkApi) => {
        try {
            thunkApi.dispatch(setPlannedWorkShiftsLoading(true));
            const shifts = await getPlannedWorkShifts(params, thunkApi.signal);
            thunkApi.dispatch(setPlannedWorkShifts(shifts));
        } finally {
            thunkApi.dispatch(setPlannedWorkShiftsLoading(false));
        }
    }
);

export const {
    setPlannedWorkShifts,
    setPlannedWorkShiftsLoading,
    reloadPlannedWorkShifts,
    reloadRoutes,
    updateWorkShiftPlanningFilters,
    setCardViewMode,
    toggleRowExpanded,
    setAllRowsExpanded,
} = workShiftPlanningSlice.actions;

export const workShiftPlanningReducer = workShiftPlanningSlice.reducer;
